import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, ImageRun,
  Table, TableRow, TableCell, WidthType, BorderStyle, Footer, PageNumber,
} from "docx";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { API, http, downloadBlob } from "./api";

const stamp = () => new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
const pick = (o, base, lang) => o?.[`${base}_${lang}`] ?? o?.[`${base}_en`] ?? "";

const dataUrlToBytes = (dataUrl) => {
  const b64 = dataUrl.split(",")[1];
  const bin = window.atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

export const verifyUrlFor = (report) =>
  report?.block?.index !== undefined ? `${window.location.origin}/?verify=${report.block.index}` : null;

async function loadBrandAssets(verifyUrl) {
  const [seal, qr] = await Promise.all([
    fetch(`${API}/assets/seal.png`)
      .then((r) => (r.ok ? r.arrayBuffer() : null))
      .catch(() => null),
    verifyUrl
      ? QRCode.toDataURL(verifyUrl, { margin: 1, width: 320, color: { dark: "#14202B", light: "#FFFFFF" } }).catch(() => null)
      : null,
  ]);
  return { sealBytes: seal ? new Uint8Array(seal) : null, qrDataUrl: qr };
}

// jsPDF renders PNG alpha as black, so flatten the seal onto white first.
async function sealOnWhite(size = 240) {
  try {
    const blob = await fetch(`${API}/assets/seal.png`).then((r) => (r.ok ? r.blob() : null));
    if (!blob) return null;
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(bitmap, 0, 0, size, size);
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

const P = (text, opts = {}) =>
  new Paragraph({
    spacing: { after: opts.after ?? 100 },
    alignment: opts.align,
    children: [new TextRun({ text, size: opts.size ?? 20, bold: opts.bold, color: opts.color, font: opts.font })],
  });

const MONO = (text) => P(text, { font: "Consolas", size: 17, color: "1F6F7A" });

const H = (text) =>
  new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 300, after: 140 },
    children: [new TextRun({ text, bold: true, size: 24 })],
  });

const cell = (text, { bold = false, width = 20 } = {}) =>
  new TableCell({
    width: { size: width, type: WidthType.PERCENTAGE },
    margins: { top: 60, bottom: 60, left: 90, right: 90 },
    children: [P(text, { size: 17, bold })],
  });

function evidenceTable(evidence) {
  const rows = [
    new TableRow({
      tableHeader: true,
      children: [
        cell("Evidence ID", { bold: true, width: 22 }),
        cell("Artifact", { bold: true, width: 26 }),
        cell("Classification", { bold: true, width: 24 }),
        cell("Score", { bold: true, width: 10 }),
        cell("Seal block", { bold: true, width: 18 }),
      ],
    }),
  ];
  (evidence || []).slice(0, 14).forEach((e) => {
    rows.push(
      new TableRow({
        children: [
          cell(e.evidence_id || "—", { width: 22 }),
          cell(e.filename || "—", { width: 26 }),
          cell(e.classification || "—", { width: 24 }),
          cell(String(e.threat_score ?? "—"), { width: 10 }),
          cell(e.sealed ? `#${e.seal_block}` : "UNSEALED", { width: 18 }),
        ],
      })
    );
  });
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 2, color: "BFC7D2" },
      bottom: { style: BorderStyle.SINGLE, size: 2, color: "BFC7D2" },
      left: { style: BorderStyle.SINGLE, size: 2, color: "BFC7D2" },
      right: { style: BorderStyle.SINGLE, size: 2, color: "BFC7D2" },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "DCE1E8" },
      insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "DCE1E8" },
    },
    rows,
  });
}

export async function exportJudicialDocx({ report, summary, evidence, matter, officer, clientHash, lang = "zh" }) {
  const generated = report?.generated_at || new Date().toUTCString();
  const chain = summary?.chain_verified ? "VERIFIED & IMMUTABLE" : "BROKEN LINK DETECTED";
  const verifyUrl = verifyUrlFor(report);
  const { sealBytes, qrDataUrl } = await loadBrandAssets(verifyUrl);
  const cover = [];
  if (sealBytes) {
    cover.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 60 },
        children: [new ImageRun({ data: sealBytes, type: "png", transformation: { width: 96, height: 96 } })],
      })
    );
  }
  const body = [
    ...cover,
    P("VERALIX DIGITAL JUSTICE ORGANIZATION", { bold: true, size: 22, align: AlignmentType.CENTER, color: "0B6E7F" }),
    P("FORENSIC JUDICIAL REPORT / 司法取证鉴定报告", { bold: true, size: 30, align: AlignmentType.CENTER, after: 60 }),
    P(`${matter?.matter_id || "—"} · ${pick(matter, "name", "en")} / ${pick(matter, "name", "zh")}`, {
      align: AlignmentType.CENTER,
      size: 18,
      color: "555F6D",
      after: 300,
    }),

    H("0. Report Control / 报告控制信息"),
    P(`Report ID / 报告编号: ${report?.report_id || "—"}`),
    P(`Generated (UTC) / 生成时间: ${generated}`),
    P(`Issuing authority / 出具机构: ${pick(matter, "authority", "en")} · ${pick(matter, "authority", "zh")}`),
    P(`Lead officer / 主办侦查员: ${matter?.lead_officer || officer?.officer_id || "—"}`),
    P(`Clearance / 权限密级: ${matter?.clearance || "LEVEL 3"} · Legal hold / 法定保全: ${matter?.legal_hold ? "ACTIVE" : "NOT SET"}`),
    P(`Risk posture / 风险态势: ${report?.risk_posture || "—"}`),

    H("1. Evidence Integrity & Seal / 证据完整性与封存"),
    P("Primary SHA-256 checksum / 主证据防伪指纹:"),
    MONO(clientHash || evidence?.[0]?.sha256 || "—"),
    P(`Checksum computed in-browser (Web Crypto) and re-computed server-side on intake. / 指纹由浏览器 Web Crypto 现场计算，接入时由服务端二次校验。`),
    P(`Ledger chain status / 哈希链状态: ${chain}`),
    P(`Ledger height / 账本高度: #${summary?.block_height ?? "—"} · Integrity score / 完整度: ${summary?.integrity_score ?? "—"}%`),
    P(`Custody sealed / 已封存: ${summary?.evidence_sealed ?? 0} / ${summary?.evidence_total ?? 0} · Court readiness / 出证就绪度: ${summary?.readiness_score ?? "—"}%`),
    P("Preservation mechanism / 保全机制: WORM append-only ledger · zero-trust isolated read-only plane."),

    H("2. AI-DFIR Analysis Findings / 智能取证研判结论"),
    P("Model engine / 推理引擎: Gemini 3 Pro (read-only advisory plane, no write scope)."),
    P(pick(report, "headline", "en") || "—", { bold: true }),
    P(pick(report, "executive_summary", "en") || "—"),
    P(pick(report, "headline", "zh") || "", { bold: true }),
    P(pick(report, "executive_summary", "zh") || ""),
    P("Key findings / 关键要点:", { bold: true }),
    ...((report?.key_findings_en || []).map((f, i) => P(`${i + 1}) ${f}`))),
    ...((report?.key_findings_zh || []).map((f, i) => P(`${i + 1}) ${f}`))),
    P("Recommended actions / 建议措施:", { bold: true }),
    ...((report?.[`recommended_actions_${lang}`] || report?.recommended_actions_en || []).map((f) => P(`→ ${f}`))),

    H("3. Evidence Inventory / 证据清单"),
    evidenceTable(evidence),
    P(""),
    P(`Integrity statement / 完整性声明: ${pick(report, "integrity_statement", "en") || "—"}`),
    P(pick(report, "integrity_statement", "zh") || ""),

    H("4. Legal Attestation / 法律确认"),
    P("Human steward assertion required / 需人类主理官断言: YES (FIDO2 hardware key)."),
    P(`Assertion operator / 签署人: ${officer?.officer_id || matter?.lead_officer || "—"}`),
    P(`Legal hold status / 法定保全状态: ${matter?.legal_hold ? "ACTIVE (Do Not Purge)" : "NOT SET"}`),
    P("This report is machine-generated from an append-only custody ledger. Any alteration of the artifacts listed above invalidates the hash chain and is detectable. / 本报告由仅追加保管链账本自动生成，上述物证任何改动都会破坏哈希链并被检出。"),
    P(""),
    P("Signature / 签名: ______________________          Date / 日期: ______________", { size: 19 }),
    P("Reviewing supervisor / 复核人: ______________________          Seal / 盖章: ______________", { size: 19 }),
    ...(qrDataUrl
      ? [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 320, after: 40 },
            children: [new ImageRun({ data: dataUrlToBytes(qrDataUrl), type: "png", transformation: { width: 104, height: 104 } })],
          }),
          P("Scan to verify the anchoring WORM block / 扫码验证本报告锚定的 WORM 区块", {
            align: AlignmentType.CENTER,
            size: 16,
            color: "5C6673",
          }),
          P(verifyUrl || "", { align: AlignmentType.CENTER, size: 16, color: "0B6E7F", font: "Consolas" }),
        ]
      : []),
  ];

  const doc = new Document({
    creator: "Veralix Aegis",
    title: `Veralix Forensic Judicial Report ${report?.report_id || ""}`,
    description: "Machine-generated judicial forensic report anchored to a WORM custody ledger",
    styles: { default: { document: { run: { font: "Calibri", size: 20 } } } },
    sections: [
      {
        properties: { page: { margin: { top: 1000, bottom: 1000, left: 1100, right: 1100 } } },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({ text: "VERALIX EVIDENCE INTEGRITY OS · OFFICIAL SEAL ATTESTATION · page ", size: 16, color: "7A8494" }),
                  new TextRun({ children: [PageNumber.CURRENT], size: 16, color: "7A8494" }),
                ],
              }),
            ],
          }),
        },
        children: body,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const filename = `VERALIX_Forensic_Report_${matter?.matter_id || "MATTER"}_${stamp()}.docx`;
  downloadBlob(filename, blob);
  return filename;
}

export async function exportJudicialPdf({ report, summary, evidence, matter, officer, clientHash }) {
  const verifyUrl = verifyUrlFor(report);
  const { qrDataUrl } = await loadBrandAssets(verifyUrl);
  const sealDataUrl = await sealOnWhite(240);
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const Hh = doc.internal.pageSize.getHeight();
  const M = 52;
  let y = M;

  const ensure = (need = 16) => {
    if (y + need < Hh - 60) return;
    doc.addPage();
    y = M;
  };
  const line = (text, { size = 10, bold = false, mono = false, color = [30, 36, 46], gap = 14 } = {}) => {
    doc.setFont(mono ? "courier" : "helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const parts = doc.splitTextToSize(String(text), W - M * 2);
    parts.forEach((p) => {
      ensure(gap);
      doc.text(p, M, y);
      y += gap;
    });
  };
  const rule = () => {
    ensure(12);
    doc.setDrawColor(190, 198, 210);
    doc.setLineWidth(0.7);
    doc.line(M, y, W - M, y);
    y += 14;
  };
  const heading = (text) => {
    y += 8;
    line(text, { size: 12.5, bold: true, color: [11, 110, 127] });
    rule();
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(11, 110, 127);
  if (sealDataUrl) {
    try {
      doc.addImage(sealDataUrl, "PNG", M, y - 12, 58, 58);
    } catch (e) {
      console.warn("Official seal could not be embedded in the PDF", e);
    }
  }
  if (qrDataUrl) {
    try {
      doc.addImage(qrDataUrl, "PNG", W - M - 58, y - 12, 58, 58);
    } catch (e) {
      console.warn("Verification QR could not be embedded in the PDF header", e);
      /* qr optional */
    }
  }
  doc.text("VERALIX DIGITAL JUSTICE ORGANIZATION", W / 2, y, { align: "center" });
  y += 20;
  doc.setFontSize(17);
  doc.setTextColor(20, 26, 34);
  doc.text("FORENSIC JUDICIAL REPORT", W / 2, y, { align: "center" });
  y += 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90, 99, 112);
  doc.text(`${matter?.matter_id || "—"} · ${matter?.name_en || ""}`, W / 2, y, { align: "center" });
  y += 22;
  rule();

  heading("0. REPORT CONTROL");
  line(`Report ID: ${report?.report_id || "—"}`);
  line(`Generated (UTC): ${report?.generated_at || new Date().toUTCString()}`);
  line(`Issuing authority: ${matter?.authority_en || "—"}`);
  line(`Lead officer: ${matter?.lead_officer || officer?.officer_id || "—"}`);
  line(`Clearance: ${matter?.clearance || "LEVEL 3"} · Legal hold: ${matter?.legal_hold ? "ACTIVE" : "NOT SET"}`);
  line(`Risk posture: ${report?.risk_posture || "—"}`);

  heading("1. EVIDENCE INTEGRITY & SEAL");
  line("Primary SHA-256 checksum:");
  line(clientHash || evidence?.[0]?.sha256 || "—", { mono: true, size: 8.6, color: [11, 110, 127] });
  line(`Ledger chain status: ${summary?.chain_verified ? "VERIFIED & IMMUTABLE" : "BROKEN LINK DETECTED"}`);
  line(`Ledger height: #${summary?.block_height ?? "—"} · Integrity score: ${summary?.integrity_score ?? "—"}%`);
  line(`Custody sealed: ${summary?.evidence_sealed ?? 0}/${summary?.evidence_total ?? 0} · Court readiness: ${summary?.readiness_score ?? "—"}%`);
  line("Preservation mechanism: WORM append-only ledger, zero-trust isolated read-only plane.");

  heading("2. AI-DFIR ANALYSIS FINDINGS");
  line("Model engine: Gemini 3 Pro (advisory only, no write scope).");
  line(report?.headline_en || "—", { bold: true });
  line(report?.executive_summary_en || "—");
  if ((report?.key_findings_en || []).length) {
    y += 4;
    line("Key findings:", { bold: true });
    report.key_findings_en.forEach((f, i) => line(`${i + 1}) ${f}`));
  }
  if ((report?.recommended_actions_en || []).length) {
    y += 4;
    line("Recommended actions:", { bold: true });
    report.recommended_actions_en.forEach((f) => line(`-> ${f}`));
  }

  heading("3. EVIDENCE INVENTORY");
  const cols = [M, M + 118, M + 250, M + 396, M + 470];
  ensure(18);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.6);
  doc.setTextColor(60, 68, 80);
  ["EVIDENCE ID", "ARTIFACT", "CLASSIFICATION", "SCORE", "SEAL"].forEach((h, i) => doc.text(h, cols[i], y));
  y += 12;
  rule();
  (evidence || []).slice(0, 18).forEach((e) => {
    ensure(14);
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.setTextColor(30, 36, 46);
    doc.text(String(e.evidence_id || "—").slice(0, 20), cols[0], y);
    doc.text(String(e.filename || "—").slice(0, 22), cols[1], y);
    doc.text(String(e.classification || "—").slice(0, 24), cols[2], y);
    doc.text(String(e.threat_score ?? "—"), cols[3], y);
    doc.text(e.sealed ? `#${e.seal_block}` : "UNSEALED", cols[4], y);
    y += 13;
  });
  y += 6;
  line(`Integrity statement: ${report?.integrity_statement_en || "—"}`);

  heading("4. LEGAL ATTESTATION");
  line("Human steward assertion required: YES (FIDO2 hardware key).");
  line(`Assertion operator: ${officer?.officer_id || matter?.lead_officer || "—"}`);
  line(`Legal hold status: ${matter?.legal_hold ? "ACTIVE (Do Not Purge)" : "NOT SET"}`);
  line("This report is machine-generated from an append-only custody ledger; altering any listed artifact breaks the hash chain and is detectable.");
  y += 12;
  line("Signature: ______________________        Date: ______________");
  line("Reviewing supervisor: ______________________        Seal: ______________");
  y += 10;
  line("Chinese-language edition: export the Word (.docx) report or the server-rendered Chinese PDF. / 中文版请导出 Word (.docx) 或中文 PDF。", {
    size: 8.4,
    color: [120, 128, 140],
  });
  if (qrDataUrl) {
    ensure(80);
    try {
      doc.addImage(qrDataUrl, "PNG", M, y, 62, 62);
    } catch (e) {
      console.warn("Verification QR could not be embedded in the PDF footer", e);
      /* qr optional */
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.4);
    doc.setTextColor(90, 99, 112);
    doc.text("Scan to verify the anchoring WORM block", M + 74, y + 22);
    doc.setFont("courier", "normal");
    doc.setTextColor(11, 110, 127);
    doc.text(doc.splitTextToSize(verifyUrl || "", W - M * 2 - 80), M + 74, y + 36);
    y += 74;
  }

  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.6);
    doc.setTextColor(140, 148, 160);
    doc.text("VERALIX EVIDENCE INTEGRITY OS · OFFICIAL SEAL ATTESTATION", M, Hh - 34);
    doc.text(`page ${p} / ${total}`, W - M, Hh - 34, { align: "right" });
  }

  const filename = `VERALIX_Judicial_Attestation_${matter?.matter_id || "MATTER"}_${stamp()}.pdf`;
  downloadBlob(filename, doc.output("blob"));
  return filename;
}

export async function exportJudicialPdfZh({ report, matter, clientHash }) {
  const { data } = await http.post(
    "/report/pdf",
    {
      report_id: report?.report_id,
      matter_id: matter?.matter_id,
      lang: "zh",
      origin: window.location.origin,
      client_hash: clientHash || null,
    },
    { responseType: "blob" }
  );
  const filename = `VERALIX_司法取证鉴定报告_${matter?.matter_id || "MATTER"}_${stamp()}.pdf`;
  downloadBlob(filename, data);
  return filename;
}
